import { buildSopVersionDiff } from '../sopVersionDiff';
import { SopVersionDiffView } from './SopVersionDiffView';
import { useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, ArrowsClockwiseIcon, XIcon } from '@phosphor-icons/react';
import { DateTime, T, Var } from 'gt-react';
import type { SopImprovementProposal } from '@erp/rpc';
import type { SopUsageEvidence } from '@erp/shared-types';
import useAuth from '@/api/auth/useAuth';
import { orpc } from '@/api/rpc/client';
import { useSopImprovementActions } from '@/api/rpc/hooks/agent/useSopImprovements';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import paths from '@/paths';

export function SopImprovementReview({
  proposal,
}: {
  proposal: SopImprovementProposal;
}) {
  const diff = buildSopVersionDiff(
    {
      steps: proposal.beforeSteps,
      parameters: proposal.beforeParameters,
      bodyMarkdown: '',
    },
    {
      steps: proposal.edit.steps,
      parameters: proposal.edit.parameters,
      bodyMarkdown: '',
    },
  ).filter((section) => section.kind !== 'bodyMarkdown');
  return (
    <div className="flex flex-col gap-3">
      <p className="whitespace-pre-wrap text-sm">{proposal.rationale}</p>
      <p className="text-sm text-muted-foreground">
        <T context="base version for a proposed SOP update">
          Proposed against published version <Var>{proposal.baseVersion}</Var>.
        </T>
      </p>
      {proposal.impactWarnings.length > 0 && (
        <AlertContainer>
          <AlertDescription>
            <ul className="flex list-disc flex-col gap-1 pl-4">
              {proposal.impactWarnings.map((warning) => (
                <li key={`${warning.code}-${warning.stepIds.join(',')}`}>
                  {warning.message}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </AlertContainer>
      )}
      <div className="overflow-x-auto">
        <SopVersionDiffView
          sections={diff}
          layout="split"
          showUnchanged={false}
        />
      </div>
      <ProposalEvidence proposal={proposal} />
      <ProposalReviewActions proposal={proposal} />
    </div>
  );
}

function ProposalReviewActions({
  proposal,
}: {
  proposal: SopImprovementProposal;
}) {
  const actions = useSopImprovementActions();
  const error =
    actions.accept.error ?? actions.dismiss.error ?? actions.regenerate.error;
  return (
    <>
      {error && (
        <AlertContainer variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </AlertContainer>
      )}
      <ProposalDraftStatus
        proposal={proposal}
        acceptedDraftVersion={actions.accept.data?.draftVersion}
      />
      <ProposalDecisionForm proposal={proposal} actions={actions} />
      {proposal.dismissalReason && <p>{proposal.dismissalReason}</p>}
    </>
  );
}

function ProposalDraftStatus({
  proposal,
  acceptedDraftVersion,
}: {
  proposal: SopImprovementProposal;
  acceptedDraftVersion?: number;
}) {
  return (
    <>
      <ProposalVersionLink
        proposal={proposal}
        acceptedDraftVersion={acceptedDraftVersion}
      />
      {proposal.published && (
        <p>
          <T context="SOP improvement already published">
            This proposal's version has been published.
          </T>
        </p>
      )}
      {proposal.draftStatus === 'deleted' && (
        <p>
          <T context="accepted SOP improvement draft was deleted">
            The accepted draft was deleted.
          </T>
        </p>
      )}
      {proposal.status === 'stale' && (
        <AlertContainer>
          <AlertDescription>
            <T context="stale SOP improvement must be regenerated">
              The procedure or source feedback changed. Regenerate the proposal
              before accepting it.
            </T>
          </AlertDescription>
        </AlertContainer>
      )}
    </>
  );
}

function canOpenProposalVersion(
  proposal: SopImprovementProposal,
  currentUserId: string | undefined,
  acceptedDraftVersion: number | undefined,
) {
  return (
    proposal.draftStatus !== 'draft' ||
    proposal.acceptedByUserId === currentUserId ||
    acceptedDraftVersion !== undefined
  );
}

function ProposalVersionLink({
  proposal,
  acceptedDraftVersion,
}: {
  proposal: SopImprovementProposal;
  acceptedDraftVersion?: number;
}) {
  const { currentUser } = useAuth();
  const draftVersion = acceptedDraftVersion ?? proposal.draftVersion;
  if (draftVersion === null || proposal.draftStatus === 'deleted') return null;
  if (!canOpenProposalVersion(proposal, currentUser?.id, acceptedDraftVersion))
    return (
      <p className="text-sm text-muted-foreground">
        <T context="accepted SOP draft is private to its reviewer">
          The reviewer who accepted this proposal has a personal draft. It
          becomes available to others after finalization.
        </T>
      </p>
    );
  return (
    <Button variant="outline" asChild>
      <Link
        to={paths.authenticated.backgroundAgents.procedureVersionDetail}
        params={{
          id: proposal.sopId,
          versionNumber: String(draftVersion),
        }}
      >
        <T context="open accepted SOP improvement draft">
          Open Version <Var>{draftVersion}</Var>
        </T>
      </Link>
    </Button>
  );
}

function DiscardedDraftRecovery({
  proposal,
  busy,
  onRegenerate,
}: {
  proposal: SopImprovementProposal;
  busy: boolean;
  onRegenerate: () => void;
}) {
  if (proposal.status !== 'accepted' || proposal.draftStatus !== 'deleted')
    return null;
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-sm text-muted-foreground">
        <T context="SOP improvement recovery after the accepted draft was discarded">
          The accepted draft was discarded. Regenerate to review a new proposal.
        </T>
      </p>
      <Button variant="outline" disabled={busy} onClick={onRegenerate}>
        <ArrowsClockwiseIcon data-icon="inline-start" />
        <T context="regenerate a SOP feedback proposal">Regenerate</T>
      </Button>
    </div>
  );
}

function ProposalDecisionForm({
  proposal,
  actions,
}: {
  proposal: SopImprovementProposal;
  actions: ReturnType<typeof useSopImprovementActions>;
}) {
  const { accept, dismiss, regenerate } = actions;
  const [reason, setReason] = useState('');
  const reasonId = useId();
  const decision = {
    proposalId: proposal.id,
    expectedRevision: proposal.reviewRevision,
  };
  const busy = accept.isPending || dismiss.isPending || regenerate.isPending;
  return (
    <>
      <DiscardedDraftRecovery
        proposal={proposal}
        busy={busy}
        onRegenerate={() => regenerate.mutate(decision)}
      />
      {(proposal.status === 'pending' || proposal.status === 'stale') && (
        <>
          <div className="flex flex-col gap-1">
            <Label htmlFor={reasonId}>
              <T context="explanation for dismissing SOP improvement">
                Dismissal Reason
              </T>
            </Label>
            <Textarea
              id={reasonId}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={4000}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {proposal.status === 'pending' && (
              <Button
                disabled={busy || Boolean(accept.data)}
                onClick={() => accept.mutate(decision)}
              >
                <CheckIcon data-icon="inline-start" />
                <T context="accept SOP proposal into a personal draft">
                  Accept Into Draft
                </T>
              </Button>
            )}
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => regenerate.mutate(decision)}
            >
              <ArrowsClockwiseIcon data-icon="inline-start" />
              <T context="regenerate a SOP feedback proposal">Regenerate</T>
            </Button>
            <Button
              variant="outline"
              disabled={busy || !reason.trim()}
              onClick={() =>
                dismiss.mutate({ ...decision, reason: reason.trim() })
              }
            >
              <XIcon data-icon="inline-start" />
              <T context="dismiss a SOP feedback proposal">Dismiss</T>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            <T context="SOP proposal acceptance does not publish">
              Accepting creates your draft. Review it on the procedure page and
              publish separately.
            </T>
          </p>
        </>
      )}
    </>
  );
}

function ProposalEvidence({ proposal }: { proposal: SopImprovementProposal }) {
  return (
    <div className="flex flex-col gap-2">
      <h4 className="font-medium">
        <T context="task sources for SOP improvement">Task Evidence</T>
      </h4>
      <Link
        className="text-sm underline"
        to="/tasks/$taskId"
        params={{ taskId: proposal.taskId }}
      >
        <T context="link from SOP improvement to originating task">
          Open Source Task
        </T>
      </Link>
      {proposal.usages.map((usage) => (
        <ObservedProcedureUsage
          key={`${usage.sourceId}-${usage.sopVersionId}`}
          usage={usage}
        />
      ))}
      {proposal.evidence.map((source) => (
        <div key={source.id} className="text-sm">
          <p>
            <DateTime>{new Date(source.createdAt)}</DateTime>
          </p>
          {source.canReadContent ? (
            <EvidenceContent receiptId={source.id} />
          ) : source.visibility === 'private' ? (
            <p className="text-muted-foreground">
              <T context="private feedback hidden from SOP reviewer">
                Private source; original content is visible only to its author.
              </T>
            </p>
          ) : (
            <p className="text-muted-foreground">
              <T context="source task permissions required to view SOP feedback">
                Source task access is required to view the original feedback.
              </T>
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function ObservedProcedureUsage({ usage }: { usage: SopUsageEvidence }) {
  const version = usage.sopVersion;
  if (version == null)
    return (
      <p className="text-sm text-muted-foreground">
        <T context="legacy task procedure evidence has no exact version">
          Procedure use recorded; exact version unknown.
        </T>
      </p>
    );
  return (
    <p className="text-sm text-muted-foreground">
      {usage.kind === 'read' ? (
        <T context="exact SOP version read during a task">
          Read version <Var>{version}</Var>
        </T>
      ) : usage.kind === 'replayed' ? (
        <T context="exact SOP version replayed during a task">
          Replayed version <Var>{version}</Var>
        </T>
      ) : (
        <T context="exact embedded SOP version engaged during a task">
          Used embedded version <Var>{version}</Var>
        </T>
      )}
    </p>
  );
}

function EvidenceContent({ receiptId }: { receiptId: string }) {
  const query = useQuery(
    orpc.agent.sopImprovements.getEvidence.queryOptions({
      input: { receiptId },
    }),
  );
  if (query.isPending)
    return (
      <p>
        <T context="loading source feedback for SOP proposal">
          Loading source…
        </T>
      </p>
    );
  if (query.error) return <p>{query.error.message}</p>;
  return (
    <div className="flex flex-col gap-1">
      <p className="whitespace-pre-wrap">
        {query.data.body ?? (
          <T context="deleted or inaccessible SOP proposal evidence">
            Source content is unavailable.
          </T>
        )}
      </p>
      {query.data.superseded && (
        <p className="text-muted-foreground">
          <T context="feedback source changed after analysis">
            This source has changed since it was analyzed.
          </T>
        </p>
      )}
    </div>
  );
}
